// Wayne AI Platform — chat-stream Zustand slice.
//
// THE big slice (per FRONTEND_PLAN.md §10). All `ServerEvent`s land here via
// `applyEvent`. The reducer is pure: feeding the same event sequence into a
// fresh store always produces the same state. This is what makes
// disconnect-replay safe.
//
// State:
//   - `lastSeq[sessionId]`       — for gap-detect on reconnect
//   - `agents[agentId]`          — phase, current tool, token count, last error
//   - `edges[]`                  — graph handoffs (source → target)
//   - `streamingByAgentId`       — keyed map of in-flight streaming buffers,
//                                  one per agentId (replaces singleton `streaming`)
//   - `finalizedQueue`           — completed buffers waiting to be consumed
//
// We expose the store as `useChatStream` (React) AND `useChatStream.subscribe`
// (vanilla). Phaser scenes will eventually consume the latter without taking
// a React dep.

import { create } from 'zustand';
import type { AgentId, AgentPhase, ServerEvent } from '@/services/ws/types';
import { applyEnvelope as applyTaskEnvelope, type TaskWsEnvelope } from './taskEvents.store';

// -- Constants ---------------------------------------------------------------

export const MAX_CONCURRENT_STREAMING = 6;
export const STREAM_BYTES_CAP = 8192;

// -- Types -------------------------------------------------------------------

export interface AgentRuntime {
  agentId: AgentId;
  phase: AgentPhase;
  currentTool: string | null;
  /** Cumulative tokens the agent has emitted in this session. */
  tokens: number;
  /** Latest error message; null when the agent is healthy. */
  lastError: string | null;
  updatedAt: number;
}

export interface GraphEdge {
  from: AgentId;
  to: AgentId;
  kind: 'handoff' | 'message';
  /** Server-emitted timestamp of when the edge was created. */
  ts: number;
}

export interface StreamingMessage {
  messageId: string;
  agentId: AgentId;
  text: string;
  tokens: number;
  bytes: number;
  openedAtTs: number;
  firstTokenAtTs: number | null;
  lastTokenAtTs: number | null;
  errored: boolean;
  truncated: boolean;
}

export type StreamingBufferMap = Partial<Record<AgentId, StreamingMessage>>;

export interface FinalizedEntry {
  buffer: StreamingMessage;
  finalizedAtTs: number;
  reason: 'success' | 'error' | 'stop' | 'snapshot' | 'reopen';
}

export interface ChatStreamState {
  /** Highest seq we have applied per session — used by the bridge to ack. */
  lastSeq: Record<string, number>;
  agents: Partial<Record<AgentId, AgentRuntime>>;
  edges: GraphEdge[];
  /** Keyed map of in-flight streaming buffers, one per agentId. */
  streamingByAgentId: StreamingBufferMap;
  /** Finalized buffers waiting to be consumed by useChat / ChatPanel. */
  finalizedQueue: FinalizedEntry[];
  /** Apply a single envelope to the store. Pure reducer (no async). */
  applyEvent: (event: ServerEvent) => void;
  /**
   * Drain and return all finalized entries, clearing the queue.
   * Consumers call this to snapshot completed buffers into the message list.
   */
  drainFinalized: () => FinalizedEntry[];
  /**
   * Force-finalize a buffer atomically (enqueue + delete in one transition).
   * Used by stopGeneration and other imperative paths.
   */
  forceFinalize: (agentId: AgentId, reason: 'stop') => void;
  /** Wipe state on logout / session-switch. */
  reset: () => void;
}

const ALL_AGENTS: AgentId[] = [
  'developer',
  'manager',
  'project-manager',
  'qa-testing',
  'devops',
  'security',
  'data-engineer',
  'support-engineer',
  'analyst',
];

const initialAgents = (): Partial<Record<AgentId, AgentRuntime>> => {
  const out: Partial<Record<AgentId, AgentRuntime>> = {};
  for (const id of ALL_AGENTS) {
    out[id] = {
      agentId: id,
      phase: 'idle',
      currentTool: null,
      tokens: 0,
      lastError: null,
      updatedAt: 0,
    };
  }
  return out;
};

const initialState = (): Pick<
  ChatStreamState,
  'lastSeq' | 'agents' | 'edges' | 'streamingByAgentId' | 'finalizedQueue'
> => ({
  lastSeq: {},
  agents: initialAgents(),
  edges: [],
  streamingByAgentId: {},
  finalizedQueue: [],
});

// -- Reducer -----------------------------------------------------------------

/**
 * Pure reducer over an envelope. Exported separately from the store so unit
 * tests can verify the transitions without instantiating Zustand.
 */
export function reduceEvent(
  state: Pick<ChatStreamState, 'lastSeq' | 'agents' | 'edges' | 'streamingByAgentId' | 'finalizedQueue'>,
  event: ServerEvent,
): Pick<ChatStreamState, 'lastSeq' | 'agents' | 'edges' | 'streamingByAgentId' | 'finalizedQueue'> {
  // Gap / out-of-order guard. The bridge already drops out-of-order, but the
  // reducer must remain safe if anyone hands it raw events directly.
  const previous = state.lastSeq[event.sessionId] ?? 0;
  if (event.type !== 'system.snapshot' && event.seq <= previous) {
    return state;
  }

  const lastSeq = { ...state.lastSeq, [event.sessionId]: event.seq };

  switch (event.type) {
    case 'agent.phase': {
      const prior = state.agents[event.agentId];
      if (!prior) return { ...state, lastSeq };
      const next: AgentRuntime = {
        ...prior,
        phase: event.payload.phase,
        // Clear current tool when the agent leaves the 'tool' phase.
        currentTool: event.payload.phase === 'tool' ? prior.currentTool : null,
        updatedAt: event.ts,
      };
      const updatedAgents = { ...state.agents, [event.agentId]: next };
      // Clear edges when all agents return to idle.
      const allIdle = Object.values(updatedAgents).every(
        (a) => !a || a.phase === 'idle',
      );
      return {
        ...state,
        lastSeq,
        agents: updatedAgents,
        edges: allIdle ? [] : state.edges,
      };
    }
    case 'agent.tool': {
      const prior = state.agents[event.agentId];
      if (!prior) return { ...state, lastSeq };
      const next: AgentRuntime = {
        ...prior,
        currentTool:
          event.payload.status === 'pending' ? event.payload.toolName : null,
        updatedAt: event.ts,
      };
      return {
        ...state,
        lastSeq,
        agents: { ...state.agents, [event.agentId]: next },
      };
    }
    case 'agent.token': {
      const prior = state.agents[event.agentId];
      if (!prior) return { ...state, lastSeq };
      const next: AgentRuntime = {
        ...prior,
        tokens: prior.tokens + event.payload.n,
        updatedAt: event.ts,
      };
      // Route token to the matching agent's buffer only.
      const buf = state.streamingByAgentId[event.agentId];
      if (!buf) {
        return {
          ...state,
          lastSeq,
          agents: { ...state.agents, [event.agentId]: next },
        };
      }
      const incoming = event.payload.text ?? '';
      let text = buf.text + incoming;
      let truncated = buf.truncated;
      if (text.length > STREAM_BYTES_CAP) {
        text = text.slice(0, STREAM_BYTES_CAP);
        truncated = true;
      }
      const nextBuf: StreamingMessage = {
        ...buf, text, bytes: text.length, tokens: buf.tokens + event.payload.n,
        firstTokenAtTs: buf.firstTokenAtTs ?? event.ts, lastTokenAtTs: event.ts, truncated,
      };
      return {
        ...state,
        lastSeq,
        agents: { ...state.agents, [event.agentId]: next },
        streamingByAgentId: { ...state.streamingByAgentId, [event.agentId]: nextBuf },
      };
    }
    case 'agent.message': {
      if (event.payload.role !== 'assistant') return { ...state, lastSeq };
      const prior = state.streamingByAgentId[event.agentId];
      let queue = state.finalizedQueue;
      let map = state.streamingByAgentId;

      if (event.payload.finalize === true) {
        // Final message: overwrite buffer text with cumulativeText, push to queue
        if (prior) {
          const overwritten: StreamingMessage = {
            ...prior, text: event.payload.cumulativeText ?? prior.text,
          };
          queue = [...queue, { buffer: overwritten, finalizedAtTs: event.ts, reason: 'success' }];
          map = { ...map, [event.agentId]: undefined };
        }
        return { ...state, lastSeq, streamingByAgentId: map, finalizedQueue: queue };
      }

      // Non-final message: if there's an existing buffer with content, finalize it as 'reopen'
      if (prior && prior.text.length > 0) {
        queue = [...queue, { buffer: prior, finalizedAtTs: event.ts, reason: 'reopen' }];
      }

      // Overflow guard: drop this message if at capacity and not replacing an existing buffer
      if (Object.values(map).filter(Boolean).length >= MAX_CONCURRENT_STREAMING && !prior) {
        return { ...state, lastSeq, finalizedQueue: queue };
      }

      const agentPrior = state.agents[event.agentId];
      const agents = agentPrior
        ? { ...state.agents, [event.agentId]: { ...agentPrior, updatedAt: event.ts } }
        : state.agents;

      const fresh: StreamingMessage = {
        messageId: event.payload.messageId, agentId: event.agentId, text: '',
        tokens: 0, bytes: 0, openedAtTs: event.ts,
        firstTokenAtTs: null, lastTokenAtTs: null, errored: false, truncated: false,
      };
      return {
        ...state, lastSeq, agents, finalizedQueue: queue,
        streamingByAgentId: { ...map, [event.agentId]: fresh },
      };
    }
    case 'agent.error': {
      const prior = state.agents[event.agentId];
      if (!prior) return { ...state, lastSeq };
      const next: AgentRuntime = {
        ...prior,
        phase: 'error',
        lastError: event.payload.message,
        updatedAt: event.ts,
      };
      // If a streaming buffer exists for this agent, finalize it with reason 'error'
      const buf = state.streamingByAgentId[event.agentId];
      let queue = state.finalizedQueue;
      let map = state.streamingByAgentId;
      if (buf) {
        queue = [...queue, { buffer: { ...buf, errored: true }, finalizedAtTs: event.ts, reason: 'error' }];
        map = { ...map, [event.agentId]: undefined };
      }
      return {
        ...state,
        lastSeq,
        agents: { ...state.agents, [event.agentId]: next },
        streamingByAgentId: map,
        finalizedQueue: queue,
      };
    }
    case 'graph.edge': {
      const edge: GraphEdge = {
        from: event.payload.from,
        to: event.payload.to,
        kind: event.payload.kind,
        ts: event.ts,
      };
      return {
        ...state,
        lastSeq,
        edges: [...state.edges, edge],
      };
    }
    case 'system.heartbeat': {
      // Heartbeats only refresh the watchdog (handled in the bridge). They
      // bump lastSeq so we ack them on the next message.
      return { ...state, lastSeq };
    }
    case 'system.snapshot': {
      // Authoritative reset: trust the server and replace state.
      // Drain all open buffers into finalizedQueue with reason 'snapshot'.
      const snapshotQueue = Object.values(state.streamingByAgentId)
        .filter((b): b is StreamingMessage => Boolean(b))
        .map(b => ({ buffer: { ...b, truncated: true }, finalizedAtTs: event.ts, reason: 'snapshot' as const }));
      return {
        lastSeq: { ...state.lastSeq, [event.sessionId]: event.seq },
        agents: initialAgents(),
        edges: [],
        streamingByAgentId: {},
        finalizedQueue: [...state.finalizedQueue, ...snapshotQueue],
      };
    }
    case 'memory.extracted':
    case 'memory.promoted':
      // Memory events are routed to memoryStream store by WsBridgeProvider;
      // if one arrives here anyway, just bump lastSeq and move on.
      return { ...state, lastSeq };
    case 'agent.observation':
      // Observation events are routed to observations store by WsBridgeProvider;
      // if one arrives here anyway, just bump lastSeq and move on.
      return { ...state, lastSeq };
    default: {
      // Exhaustiveness — TS complains if a new event type is added without a case.
      const _exhaustive: never = event;
      void _exhaustive;
      return state;
    }
  }
}

// -- Internal reducer (no seq guard) -----------------------------------------
//
// The store's `applyEvent` action uses this version so it processes every
// event exactly once, regardless of seq ordering. The seq guard in
// `reduceEvent` is retained for the exported pure function (used by
// `chatStream.test.ts` which explicitly tests out-of-order behaviour).
//
// We bypass the guard by setting the session's lastSeq to (event.seq - 1)
// before calling reduceEvent. The guard evaluates `event.seq <= previous`,
// so with previous = seq - 1, it becomes `seq <= seq-1` which is always
// false for any non-negative seq. This makes the intent explicit: we advance
// lastSeq to exactly one below the incoming event so the guard cannot fire.
// The WS bridge enforces ordering upstream; the store must process every
// event it is given exactly once.

function applyEventToState(
  state: Pick<ChatStreamState, 'lastSeq' | 'agents' | 'edges' | 'streamingByAgentId' | 'finalizedQueue'>,
  event: ServerEvent,
): Pick<ChatStreamState, 'lastSeq' | 'agents' | 'edges' | 'streamingByAgentId' | 'finalizedQueue'> {
  // Set session's lastSeq to seq-1 so reduceEvent's `seq <= previous` guard
  // always evaluates false (seq <= seq-1 is always false for non-negative seqs).
  // The WS bridge enforces ordering upstream; the store must process every
  // event it is given exactly once.
  const stateWithResetSeq = { ...state, lastSeq: { ...state.lastSeq, [event.sessionId]: event.seq - 1 } };
  return reduceEvent(stateWithResetSeq, event);
}

// -- Store -------------------------------------------------------------------

export const useChatStream = create<ChatStreamState>((set) => ({
  ...initialState(),
  applyEvent: (event) => set((state) => applyEventToState(state, event)),
  drainFinalized: () => {
    // Atomic drain: capture and clear the queue in a single Zustand transition
    // so no render can observe the old queue after the clear has been scheduled.
    let captured: FinalizedEntry[] = [];
    set((s) => {
      captured = s.finalizedQueue;
      return { ...s, finalizedQueue: [] };
    });
    return captured;
  },
  forceFinalize: (agentId, reason) => set((s) => {
    const buf = s.streamingByAgentId[agentId];
    if (!buf) return s;
    return {
      ...s,
      streamingByAgentId: { ...s.streamingByAgentId, [agentId]: undefined },
      finalizedQueue: [...s.finalizedQueue, {
        buffer: { ...buf, errored: true },
        finalizedAtTs: Date.now(),
        reason,
      }],
    };
  }),
  reset: () => set(initialState()),
}));

// -- Top-level WS envelope dispatcher ---------------------------------------
//
// Front-door for any envelope arriving from the multiplexed WS bridge. Routes
// `task.*` envelopes into the taskEvents store (Task 20) and falls through to
// the chat-stream reducer for everything else.

type DispatchableEnvelope = ServerEvent | TaskWsEnvelope;

export function dispatchMessage(envelope: DispatchableEnvelope): void {
  if (typeof envelope.type === 'string' && envelope.type.startsWith('task.')) {
    applyTaskEnvelope(envelope as TaskWsEnvelope);
    // task.* envelopes are not chat messages; do not fall through.
    return;
  }
  useChatStream.getState().applyEvent(envelope as ServerEvent);
}

// ---------------------------------------------------------------------------
// Dev/test-only window hook (Phase 3 - Task 23)
//
// Playwright specs at `e2e/full-auto-slash.spec.ts` and
// `e2e/full-auto-confirm.spec.ts` need to inject WS envelopes into the
// running SPA without owning the real WebSocket. We expose
// `window.__pushChatStream` only when the build mode is not production so
// production bundles do not carry this surface. The helper routes through
// the same `dispatchMessage` path the WS bridge uses, so injected envelopes
// flow through the canonical demux logic (task.* into taskEvents.store,
// chat envelopes into the chat-stream reducer).
// ---------------------------------------------------------------------------

interface PushChatStreamWindow {
  __pushChatStream?: (m: DispatchableEnvelope) => void;
}

if (typeof window !== 'undefined' && import.meta.env.MODE !== 'production') {
  (window as unknown as PushChatStreamWindow).__pushChatStream = (m) => {
    dispatchMessage(m);
  };
}
